"use client";

import { useAuth } from "@/components/AuthContext";
import TranscriptionView from "@/components/TranscriptionView";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import AISchoolView from "@/components/school/AISchoolView";
import { SpatiusPrimarySessionRoot } from "@/components/spatius-avatar";
import { Button } from "@/components/ui/button";
import { ZenProvider } from "@/components/zentutor/ZenContext";
import ZenInterface from "@/components/zentutor/ZenInterface";
import { VoiceKey } from "@/data/voiceDisplayNames";
import useLiveKitAgent, { AgentConnectionState } from "@/hooks/useLiveKitAgent";
import {
  getUserFacingAudioMessage,
  getUserFacingConnectionFailureMessage,
} from "@/lib/livekit-user-facing-messages";
import { createLogger } from "@/lib/logger";
import { Tutor } from "@/types/tutor";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";

// Use a darker, premium background for the school
const SCHOOL_BG = "bg-[#0F0F14]";

const VALID_VOICES: VoiceKey[] = [
  "Puck",
  "Charon",
  "Kore",
  "Fenrir",
  "Aoede",
  "Leda",
  "Orus",
  "Zephyr",
];

const LEGACY_VOICE_MAP: Record<string, VoiceKey> = {
  alloy: "Orus",
  echo: "Charon",
  shimmer: "Aoede",
};

function normalizeTutorVoice(input?: string | null): VoiceKey | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  if ((VALID_VOICES as string[]).includes(trimmed)) return trimmed as VoiceKey;
  const mappedLegacy = LEGACY_VOICE_MAP[trimmed.toLowerCase()];
  return mappedLegacy || null;
}

export default function SchoolWorkspacePage() {
  const logger = createLogger("SchoolPage");
  const { user, openAuthModal } = useAuth();

  // State for the selected tutor and session
  const [activeTutor, setActiveTutor] = useState<Tutor | null>(null);
  const [activeTutorCurriculumItemId, setActiveTutorCurriculumItemId] = useState<string | null>(
    null
  );
  const [pendingTutorStart, setPendingTutorStart] = useState<{
    tutor: Tutor;
    tutorCurriculumItemId?: string | null;
  } | null>(null);
  const [isZenMicMuted, setIsZenMicMuted] = useState(false);

  // Config for the LiveKit agent
  const [agentConfig, setAgentConfig] = useState({
    languageCode: "en-US",
    languageLevel: "intermediate",
    voice: "Aoede", // Default, will be overridden by tutor
    teachingStyle: "neutral", // Default
  });

  // Use the LiveKit hook
  const {
    room,
    connectToLiveKit,
    disconnectFromLiveKit,
    connectionState,
    isRecording,
    toggleRecording,
    recordingTime,
    audioTrackError,
    setAudioTrackError,
    audioRetryCount,
    retryAudioTrack,
    setConnectionError, // To clear errors
    connectionError,
    baseRoom,
    spatiusPrimarySession,
    handleSpatiusPrimaryConnected,
    handleSpatiusPrimaryDisconnected,
    handleSpatiusPrimaryError,
    handleSpatiusPrimaryStateChange,
  } = useLiveKitAgent({
    selectedLanguageCode: agentConfig.languageCode,
    selectedLanguageLevel: agentConfig.languageLevel,
    selectedVoice: agentConfig.voice,
    selectedTeachingStyle: agentConfig.teachingStyle,
    sessionMode: activeTutor?.experience_type === "zen" ? "zen" : "conversation",
    setInteractionMode: () => {}, // Not used in this simplified view yet
    subscribed: true, // Assume subscribed for school for now, or fetch from auth
    // Pass the tutor marketplace params!
    tutorId: activeTutor?.id,
    tutorPrompt: activeTutor?.system_prompt,
    tutorCurriculumItemId: activeTutorCurriculumItemId,
  });

  const startTutorSession = (tutor: Tutor, tutorCurriculumItemId?: string | null) => {
    logger.info("Starting session with tutor", {
      tutorId: tutor.id,
      name: tutor.name,
      tutorCurriculumItemId: tutorCurriculumItemId || null,
    });
    setActiveTutor(tutor);
    setActiveTutorCurriculumItemId(tutorCurriculumItemId || null);

    // Update config based on tutor if needed (e.g. if tutor defines a specific voice)
    // For now we keep user defaults or generic defaults, but we could map tutor.voice_id
    const normalizedTutorVoice = normalizeTutorVoice(tutor.voice_id);
    setAgentConfig((prev) => ({
      ...prev,
      voice: normalizedTutorVoice || prev.voice,
      // We could also map teaching style if tutor has it
    }));
  };

  // Handle starting a session from the marketplace
  const handleStartSession = (tutor: Tutor, tutorCurriculumItemId?: string | null) => {
    if (!user) {
      logger.info("Unauthenticated user attempted to start school session", {
        tutorId: tutor.id,
        name: tutor.name,
        tutorCurriculumItemId: tutorCurriculumItemId || null,
      });
      setPendingTutorStart({ tutor, tutorCurriculumItemId });
      openAuthModal();
      return;
    }

    startTutorSession(tutor, tutorCurriculumItemId);
  };

  // If user authenticates after clicking "start", continue with the pending tutor.
  useEffect(() => {
    if (!user || !pendingTutorStart) return;
    startTutorSession(pendingTutorStart.tutor, pendingTutorStart.tutorCurriculumItemId);
    setPendingTutorStart(null);
  }, [user, pendingTutorStart]);

  // Start connection when activeTutor is set
  useEffect(() => {
    if (activeTutor && !isRecording && connectionState === "disconnected") {
      const mode = activeTutor.experience_type === "zen" ? "zen" : "conversation";
      logger.info(`Auto-connecting to ${mode} mode for tutor ${activeTutor.name}`);
      // Use toggleRecording so hook updates isRecording/session timer state correctly.
      void toggleRecording(mode);
    }
  }, [activeTutor, isRecording, connectionState, toggleRecording, logger]);

  // Handle manual disconnect or exit
  const handleExitSession = () => {
    disconnectFromLiveKit(false, false, "school_exit_session");
    setIsZenMicMuted(false);
    setActiveTutor(null);
    setActiveTutorCurriculumItemId(null);
  };

  const handleToggleZenMute = async () => {
    if (!room) return;

    const nextMuted = !isZenMicMuted;
    try {
      // Local mic should be enabled when not muted.
      await room.localParticipant.setMicrophoneEnabled(!nextMuted);
      setIsZenMicMuted(nextMuted);
    } catch (error) {
      logger.error("Failed to toggle zen microphone", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // ------------------------------------------------------------------
  // Render: Active Session
  // ------------------------------------------------------------------
  if (activeTutor) {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col ${SCHOOL_BG} text-white animate-in fade-in duration-300`}
      >
        <SpatiusPrimarySessionRoot
          baseRoom={baseRoom}
          onConnected={handleSpatiusPrimaryConnected}
          onDisconnected={handleSpatiusPrimaryDisconnected}
          onError={handleSpatiusPrimaryError}
          onStateChange={handleSpatiusPrimaryStateChange}
          session={spatiusPrimarySession}
        >
          {/* CRITICAL: RoomAudioRenderer plays the agent's audio */}
          <TrackedRoomAudioRenderer
            activeSession={isRecording}
            roomName={room.name || null}
            surface="school"
          />
          {/* Header / Exit Button */}
          <div className="absolute top-4 right-4 z-[100]">
            <Button
              onClick={handleExitSession}
              className="h-10 w-10 rounded-lg border border-white/10 bg-[#15161B] p-0 text-white/70 hover:border-white/22 hover:bg-[#1B1C22] hover:text-white"
            >
              <X size={24} />
            </Button>
          </div>

          {/* Session Content */}
          <div className="flex-1 w-full h-full relative">
            {activeTutor.experience_type === "zen" ? (
              <ZenProvider>
                <div className="w-full h-full">
                  {(connectionState === "connecting" ||
                    connectionState === "reconnecting" ||
                    connectionState === "failed" ||
                    connectionError ||
                    audioTrackError) && (
                    <div className="absolute left-4 top-4 z-[120] max-w-md rounded-lg border border-white/15 bg-[#15161B]/96 px-4 py-3 text-sm">
                      <div className="font-semibold text-white mb-1">
                        {connectionState === "connecting" || connectionState === "reconnecting"
                          ? "Getting your tutor ready..."
                          : connectionState === "failed"
                            ? "We couldn't connect to your tutor"
                            : "Your audio needs attention"}
                      </div>
                      {connectionState === "failed" && connectionError && (
                        <div className="text-red-300 text-xs mb-2">
                          {getUserFacingConnectionFailureMessage(connectionError)}
                        </div>
                      )}
                      {audioTrackError && (
                        <div className="text-amber-300 text-xs mb-2">
                          {getUserFacingAudioMessage(audioTrackError)}
                        </div>
                      )}
                      {(connectionState === "failed" || audioTrackError) && (
                        <div className="flex gap-2 mt-2">
                          <Button
                            onClick={() => toggleRecording("zen")}
                            className="h-8 px-3 text-xs bg-white text-black hover:bg-gray-200"
                          >
                            Try session again
                          </Button>
                          {audioTrackError && (
                            <Button
                              onClick={retryAudioTrack}
                              className="h-8 px-3 text-xs bg-white/10 text-white hover:bg-white/20 border border-white/20"
                            >
                              Try audio again
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Wrapping ZenInterface to ensure full height */}
                  <ZenInterface isMuted={isZenMicMuted} onToggleMute={handleToggleZenMute} />
                  {/* Note: ZenInterface uses `useTracks` internally so it needs RoomContext. */}
                </div>
              </ZenProvider>
            ) : (
              <div className="h-full w-full bg-[#0F0F14]">
                {/* Conversation / Transcription View */}
                {/* We reuse TranscriptionView but hide some complexity if possible */}
                <TranscriptionView
                  onClose={handleExitSession}
                  connectionState={connectionState}
                  connectionError={connectionError}
                  retryConnection={() => toggleRecording("conversation")}
                  audioTrackError={audioTrackError}
                  retryAudioTrack={retryAudioTrack}
                  clearAudioErrors={() => setAudioTrackError(null)}
                  sessionDuration={recordingTime}
                  isActiveSession={isRecording}
                  showReadyStateWhenInactive={true}
                  schoolMode={true}
                  assistantDisplayName={activeTutor.name}
                  selectedLanguageCode={agentConfig.languageCode}
                  // ... other props
                />
              </div>
            )}
          </div>
        </SpatiusPrimarySessionRoot>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Render: Marketplace (Default)
  // ------------------------------------------------------------------
  return (
    <div className={`min-h-screen ${SCHOOL_BG}`}>
      <AISchoolView onStartSession={handleStartSession} />
    </div>
  );
}
